import React, { useEffect, useState } from 'react';
import { Layout, Menu, Button, Space, Avatar, message, Drawer, Tooltip } from 'antd';
import useSessionRefresh from '../../hooks/useSessionRefresh';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  TeamOutlined,
  DollarOutlined,
  ShopOutlined,
  MenuOutlined,
  BellOutlined,
  AppstoreOutlined,
  ApiOutlined,
  GlobalOutlined,
  FileTextOutlined,
  MailOutlined,
  HistoryOutlined,
  BankOutlined,
  WalletOutlined,
  RocketOutlined,
  GiftOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet, Link } from 'react-router-dom';
import styled from 'styled-components';
import { apiClient, API_ENDPOINTS } from '../../utils/api';
import type { MenuProps } from 'antd';
import ReactCountryFlag from 'react-country-flag';
import { DashboardProvider } from '../../context/DashboardContext';

const { Header, Sider, Content } = Layout;
// Note: React requires REACT_APP_ prefix for environment variables to be accessible in the browser
// In .env file, use: REACT_APP_ONCHAIN_WATCHER_FRONTEND_URL=http://localhost:19222
// Backward compatible: falls back to REACT_APP_ONCHAIN_WATCHER_URL if older envs are still in use.
const WATCHER_URL =
  process.env.REACT_APP_ONCHAIN_WATCHER_FRONTEND_URL ||
  process.env.REACT_APP_ONCHAIN_WATCHER_URL ||
  '';

// New Theme Colors
const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  accentDim: 'rgba(34, 211, 238, 0.1)',
  hover: '#1f2a37',
  gold: '#D4AF37',
  goldDim: 'rgba(212, 175, 55, 0.15)',
  goldHover: 'rgba(212, 175, 55, 0.25)',
};

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: ${COLORS.bgPrimary};
`;

const StyledHeader = styled(Header)`
  background: ${COLORS.bgSecondary} !important;
  border-bottom: 1px solid ${COLORS.border};
  padding: 0 24px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 20;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  img {
    width: 32px;
    height: 32px;
  }
  
  .logo-text {
    font-size: 20px;
    font-weight: 700;
    color: ${COLORS.accent};
    
    @media (max-width: 640px) {
      display: none;
    }
  }
  
  .badge {
    font-size: 12px;
    font-family: monospace;
    color: #6b7280;
    background: #0D1117;
    padding: 2px 8px;
    border-radius: 6px;
    border: 1px solid ${COLORS.border};
    
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

const RightSection = styled(Space)`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 12px;
    
    .logout-button {
      width: 44px !important;
      height: 44px !important;
      font-size: 20px !important;
      min-width: 44px !important;
    }
  }
`;

const NotificationBtn = styled.button`
  position: relative;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: ${COLORS.hover};
    color: ${COLORS.textPrimary};
  }
  
  .count {
    position: absolute;
    top: 0;
    right: 0;
    background: #dc2626;
    color: white;
    font-size: 10px;
    font-weight: bold;
    min-width: 16px;
    height: 16px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid ${COLORS.bgSecondary};
  }
`;

/* UserProfile styled component removed */

const StyledMenuLink = styled(Link)`
  color: ${COLORS.textPrimary} !important;
  text-decoration: none;
  display: block;
  width: 100%;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${COLORS.gold} !important;
  }
  
  &:active {
    color: ${COLORS.gold} !important;
  }
`;

const LaunchWatcherButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: linear-gradient(135deg, ${COLORS.accent}20 0%, ${COLORS.goldDim} 100%);
  border: 1px solid ${COLORS.accent}50;
  border-radius: 6px;
  color: ${COLORS.accent};
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: linear-gradient(135deg, ${COLORS.accent}30 0%, ${COLORS.gold}30 100%);
    border-color: ${COLORS.accent};
    box-shadow: 0 2px 8px ${COLORS.accent}30;
  }
  
  .icon {
    font-size: 14px;
  }
`;

const StyledSider = styled(Sider)`
  background: ${COLORS.bgSecondary} !important;
  border-right: 1px solid ${COLORS.border};
  overflow-y: auto;
  height: calc(100vh - 64px);
  position: sticky;
  top: 64px;
  
  @media (max-width: 1024px) {
    display: none !important;
  }
  
  .ant-menu {
    border-right: none;
    background: transparent !important;
    padding: 16px 8px;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-menu-item {
    color: ${COLORS.textPrimary} !important;
    border-radius: 8px;
    margin: 4px 0 !important;
    transition: all 0.2s ease;
    
    &:hover {
      background: ${COLORS.goldDim} !important;
      color: ${COLORS.gold} !important;
      
      a {
        color: ${COLORS.gold} !important;
      }
    }
    
    &.ant-menu-item-selected {
      background: ${COLORS.goldDim} !important;
      color: ${COLORS.gold} !important;
      border-left: 3px solid ${COLORS.gold} !important;
      
      &::after {
        display: none !important;
      }
      
      a {
        color: ${COLORS.gold} !important;
        font-weight: 500;
      }
    }
  }
  
  .ant-menu-submenu {
    .ant-menu-submenu-title {
      color: ${COLORS.textPrimary} !important;
      border-radius: 8px;
      margin: 4px 0 !important;
      transition: all 0.2s ease;
      
      .ant-menu-submenu-arrow {
        color: ${COLORS.textSecondary} !important;
        transition: all 0.2s ease;
        
        &::before,
        &::after {
          background: ${COLORS.textSecondary} !important;
        }
      }
      
      &:hover {
        background: ${COLORS.goldDim} !important;
        color: ${COLORS.gold} !important;
        
        .ant-menu-submenu-arrow {
          color: ${COLORS.gold} !important;
          
          &::before,
          &::after {
            background: ${COLORS.gold} !important;
          }
        }
      }
    }
    
    &.ant-menu-submenu-open {
      .ant-menu-submenu-title {
        color: ${COLORS.gold} !important;
        
        .ant-menu-submenu-arrow {
          color: ${COLORS.gold} !important;
          
          &::before,
          &::after {
            background: ${COLORS.gold} !important;
          }
        }
      }
    }
    
    .ant-menu {
      background: ${COLORS.bgPrimary} !important;
      padding-left: 8px;
      
      .ant-menu-item {
        margin-left: 0 !important;
        padding-left: 40px !important;
        
        &:hover {
          background: ${COLORS.goldHover} !important;
        }
        
        &.ant-menu-item-selected {
          background: ${COLORS.goldDim} !important;
          border-left: 3px solid ${COLORS.gold} !important;
        }
      }
    }
  }
  
  .ant-menu-item-icon {
    color: inherit !important;
  }
`;

const StyledContent = styled(Content)`
  padding: 24px;
  overflow-y: auto;
  background: ${COLORS.bgPrimary};
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const MobileMenuBtn = styled(Button)`
  display: none !important;
  border: none;
  background: transparent;
  color: ${COLORS.textPrimary};
  font-size: 20px;
  padding: 4px;
  
  @media (max-width: 1024px) {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }
  
  &:hover, &:focus {
    color: ${COLORS.accent} !important;
    background: ${COLORS.hover} !important;
  }
`;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedKeys, setSelectedKeys] = useState<string[]>(['dashboard']);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [sessionExpiryTime, setSessionExpiryTime] = useState<number | null>(null);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const [, setNotifications] = useState<any[]>([]);

  useEffect(() => {
    // Check if user has both accessToken and accessSecret - if not, redirect to login
    const accessToken = localStorage.getItem('accessToken');
    const accessSecret = localStorage.getItem('accessSecret');

    if (!accessToken || !accessSecret) {
      message.warning('Please log in to access your dashboard.');
      navigate('/?userType=Administrator');
      return;
    }

    // Close mobile menu when route changes
    setMobileMenuVisible(false);

    // Update selected menu item based on current route
    const path = location.pathname;
    if (path.includes('/users/Administrators')) {
      setSelectedKeys(['users-administrator']);
    } else if (path.includes('/users/Merchants')) {
      setSelectedKeys(['users-merchant']);
    } else if (path.includes('/pending-users')) {
      setSelectedKeys(['pending-users']);
    } else if (path.includes('/companies')) {
      setSelectedKeys(['companies']);
    } else if (path.includes('/currencies')) {
      setSelectedKeys(['currencies']);
      setOpenKeys(['blockchain']);
    } else if (path.includes('/blockchain-networks')) {
      setSelectedKeys(['blockchain-networks']);
      setOpenKeys(['blockchain']);
    } else if (path.includes('/contract-addresses')) {
      setSelectedKeys(['contract-addresses']);
      setOpenKeys(['blockchain']);
    } else if (path.includes('/rpc-endpoints')) {
      setSelectedKeys(['rpc-endpoints']);
      setOpenKeys(['blockchain']);
    } else if (path.includes('/email-templates')) {
      setSelectedKeys(['email-templates']);
      setOpenKeys(['email-service']);
    } else if (path.includes('/email-logs')) {
      setSelectedKeys(['email-logs']);
      setOpenKeys(['email-service']);
    } else if (path.includes('/birthday-emails')) {
      setSelectedKeys(['birthday-emails']);
      setOpenKeys(['email-service']);
    } else if (path.includes('/internal-emails')) {
      setSelectedKeys(['internal-emails']);
      setOpenKeys(['email-service']);
    } else if (path.includes('/credit-tokens')) {
      setSelectedKeys(['credit-tokens']);
      setOpenKeys(['billing']);
    } else if (path.includes('/login-history')) {
      setSelectedKeys(['login-history']);
    } else if (path.includes('/critical-notifications')) {
      setSelectedKeys(['critical-notifications']);
      setOpenKeys(['notifications']);
    } else if (path.includes('/merchant-update-requests')) {
      setSelectedKeys(['merchant-update-requests']);
      setOpenKeys(['notifications']);
    } else if (path.includes('/webhooks')) {
      setSelectedKeys(['webhooks']);
      setOpenKeys(['notifications']);
    } else if (path.includes('/wallets')) {
      setSelectedKeys(['wallets']);
    } else if (path.includes('/sweep-list')) {
      setSelectedKeys(['sweep-list']);
    } else if (path.includes('/mpc-service')) {
      setSelectedKeys(['mpc-service']);
    } else if (path.includes('/settings')) {
      setSelectedKeys(['settings']);
    } else if (path.includes('/profile')) {
      setSelectedKeys(['profile']);
    } else {
      setSelectedKeys(['dashboard']);
    }
  }, [location.pathname, navigate]);

  // Fetch user profile, session expiry, and notification count
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_ACCOUNT_PROFILE);
        if (response.data?.status === 'success' && response.data?.data) {
          setUserProfile(response.data.data);
        }
      } catch (error: any) {
        console.error('Failed to fetch user profile:', error);
      }
    };

    const fetchNotificationCount = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_NOTIFICATION_STATS || '/api/Administrator/critical-notifications/stats');
        if (response.data?.status === 'success' && response.data?.data) {
          setNotificationCount(response.data.data.unreadCount || 0);
        }
      } catch (error: any) {
        console.error('Failed to fetch notification count:', error);
      }
    };

    const fetchRecentNotifications = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_CRITICAL_NOTIFICATIONS, {
          params: {
            pageId: 1,
            perPage: 10,
            sort: 'DESC',
            isSeen: false,
          },
        });
        if (response.data?.status === 'success' && response.data?.data) {
          const items = response.data.data.itemsList || response.data.data.items || (Array.isArray(response.data.data) ? response.data.data : []);
          setNotifications(items);
        }
      } catch (error: any) {
        console.error('Failed to fetch recent notifications:', error);
      }
    };

    // Get session expiry time from localStorage
    const expiryTime = localStorage.getItem('sessionExpiryTime');
    if (expiryTime) {
      setSessionExpiryTime(parseInt(expiryTime, 10));
    }

    fetchUserProfile();
    fetchNotificationCount();
    fetchRecentNotifications();

    // Refresh notification count and list every 30 seconds
    const interval = setInterval(() => {
      fetchNotificationCount();
      fetchRecentNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Refresh notification count when navigating to/from notifications page
  useEffect(() => {
    if (location.pathname.includes('/critical-notifications')) {
      const fetchNotificationCount = async () => {
        try {
          const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_NOTIFICATION_STATS || '/api/Administrator/critical-notifications/stats');
          if (response.data?.status === 'success' && response.data?.data) {
            setNotificationCount(response.data.data.unreadCount || 0);
          }
        } catch (error: any) {
          console.error('Failed to fetch notification count:', error);
        }
      };
      fetchNotificationCount();
    }
  }, [location.pathname]);



  const handleSessionExpire = () => {
    message.warning('Your session has expired. Please log in again.');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('accessSecret');
    localStorage.removeItem('userType');
    localStorage.removeItem('sessionExpiryTime');
    navigate('/?userType=Administrator');
  };

  // Periodically refresh the session token to keep Redis accessSecret alive
  useSessionRefresh('Administrator', handleSessionExpire);

  const handleLogout = async () => {
    try {
      await apiClient.post(API_ENDPOINTS.LOGOUT_ADMIN);
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('accessSecret');
      localStorage.removeItem('userType');
      localStorage.removeItem('sessionExpiryTime');
      
      // Capture current route (pathname + search + hash) for continue redirect
      const currentRoute = location.pathname + location.search + location.hash;
      // Encode the route to handle special characters
      const continueParam = encodeURIComponent(currentRoute);
      navigate(`/?userType=Administrator&continue=${continueParam}`);
    }
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const key = e.key;
    setMobileMenuVisible(false);
    
    if (key === 'logout') {
      handleLogout();
      return;
    }

    if (key === 'dashboard') {
      navigate('/kivu');
    } else if (key === 'users-administrator') {
      navigate('/kivu/users/Administrators');
    } else if (key === 'users-merchant') {
      navigate('/kivu/users/Merchants');
    } else if (key === 'pending-users') {
      navigate('/kivu/pending-users');
    } else if (key === 'companies') {
      navigate('/kivu/companies');
    } else if (key === 'currencies') {
      navigate('/kivu/currencies');
    } else if (key === 'blockchain-networks') {
      navigate('/kivu/blockchain-networks');
    } else if (key === 'contract-addresses') {
      navigate('/kivu/contract-addresses');
    } else if (key === 'rpc-endpoints') {
      navigate('/kivu/rpc-endpoints');
    } else if (key === 'email-templates') {
      navigate('/kivu/email-templates');
    } else if (key === 'email-logs') {
      navigate('/kivu/email-logs');
    } else if (key === 'login-history') {
      navigate('/kivu/login-history');
    } else if (key === 'critical-notifications') {
      navigate('/kivu/critical-notifications');
    } else if (key === 'merchant-update-requests') {
      navigate('/kivu/merchant-update-requests');
    } else if (key === 'wallets') {
      navigate('/kivu/wallets');
    } else if (key === 'sweep-list') {
      navigate('/kivu/sweep-list');
    } else if (key === 'mpc-service') {
      navigate('/kivu/mpc-service');
    } else if (key === 'settings') {
      navigate('/kivu/settings');
    } else if (key === 'webhooks') {
      navigate('/kivu/webhooks');
    } else if (key === 'profile') {
      navigate('/kivu/profile');
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'dashboard',
      icon: <AppstoreOutlined />,
      label: <StyledMenuLink to="/kivu" onClick={(e) => e.stopPropagation()}>Dashboard</StyledMenuLink>,
    },
    {
      key: 'users',
      icon: <TeamOutlined />,
      label: 'Users',
      children: [
        {
          key: 'users-administrator',
          icon: <UserOutlined />,
          label: <StyledMenuLink to="/kivu/users/Administrators" onClick={(e) => e.stopPropagation()}>Administrators</StyledMenuLink>,
        },
        {
          key: 'users-merchant',
          icon: <ShopOutlined />,
          label: <StyledMenuLink to="/kivu/users/Merchants" onClick={(e) => e.stopPropagation()}>Merchants</StyledMenuLink>,
        },
        {
          key: 'pending-users',
          icon: <TeamOutlined />,
          label: <StyledMenuLink to="/kivu/pending-users" onClick={(e) => e.stopPropagation()}>Pending Users</StyledMenuLink>,
        },
        {
          key: 'login-history',
          icon: <HistoryOutlined />,
          label: <StyledMenuLink to="/kivu/login-history" onClick={(e) => e.stopPropagation()}>Login History</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'companies',
      icon: <BankOutlined />,
      label: <StyledMenuLink to="/kivu/companies" onClick={(e) => e.stopPropagation()}>Companies</StyledMenuLink>,
    },
    {
      key: 'blockchain',
      icon: <GlobalOutlined />,
      label: 'Blockchain',
      children: [
        {
          key: 'currencies',
          icon: <DollarOutlined />,
          label: <StyledMenuLink to="/kivu/currencies" onClick={(e) => e.stopPropagation()}>Assets</StyledMenuLink>,
        },
        {
          key: 'blockchain-networks',
          icon: <GlobalOutlined />,
          label: <StyledMenuLink to="/kivu/blockchain-networks" onClick={(e) => e.stopPropagation()}>Networks</StyledMenuLink>,
        },
        {
          key: 'contract-addresses',
          icon: <FileTextOutlined />,
          label: <StyledMenuLink to="/kivu/contract-addresses" onClick={(e) => e.stopPropagation()}>Contracts</StyledMenuLink>,
        },
        {
          key: 'rpc-endpoints',
          icon: <ApiOutlined />,
          label: <StyledMenuLink to="/kivu/rpc-endpoints" onClick={(e) => e.stopPropagation()}>RPC Endpoints</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'email-service',
      icon: <MailOutlined />,
      label: 'Email Service',
      children: [
        {
          key: 'email-templates',
          label: <StyledMenuLink to="/kivu/email-templates" onClick={(e) => e.stopPropagation()}>Email Templates</StyledMenuLink>,
        },
        {
          key: 'email-logs',
          label: <StyledMenuLink to="/kivu/email-logs" onClick={(e) => e.stopPropagation()}>Email Logs</StyledMenuLink>,
        },
        {
          key: 'birthday-emails',
          label: <StyledMenuLink to="/kivu/birthday-emails" onClick={(e) => e.stopPropagation()}>Birthday Emails</StyledMenuLink>,
        },
        {
          key: 'internal-emails',
          label: <StyledMenuLink to="/kivu/internal-emails" onClick={(e) => e.stopPropagation()}>Internal Emails</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'billing',
      icon: <DollarOutlined />,
      label: 'Billing',
      children: [
        {
          key: 'subscription-plans',
          icon: <DollarOutlined />,
          label: <StyledMenuLink to="/kivu/subscription-plans" onClick={(e) => e.stopPropagation()}>Subscription Plans</StyledMenuLink>,
        },
        {
          key: 'invoices',
          icon: <FileTextOutlined />,
          label: <StyledMenuLink to="/kivu/invoices" onClick={(e) => e.stopPropagation()}>Invoices</StyledMenuLink>,
        },
        {
          key: 'credit-tokens',
          icon: <GiftOutlined />,
          label: <StyledMenuLink to="/kivu/credit-tokens" onClick={(e) => e.stopPropagation()}>Credit Tokens</StyledMenuLink>,
        },
        {
          key: 'billing-reports',
          icon: <BarChartOutlined />,
          label: <StyledMenuLink to="/kivu/billing-reports" onClick={(e) => e.stopPropagation()}>Billing Reports</StyledMenuLink>,
        },
        {
          key: 'vat-configurations',
          icon: <FileTextOutlined />,
          label: <StyledMenuLink to="/kivu/vat-configurations" onClick={(e) => e.stopPropagation()}>VAT Configurations</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'wallets',
      icon: <WalletOutlined />,
      label: <StyledMenuLink to="/kivu/wallets" onClick={(e) => e.stopPropagation()}>Wallets</StyledMenuLink>,
    },
    {
      key: 'sweep-list',
      icon: <WalletOutlined />,
      label: <StyledMenuLink to="/kivu/sweep-list" onClick={(e) => e.stopPropagation()}>Sweep Remittance</StyledMenuLink>,
    },
    {
      key: 'mpc-service',
      icon: <ApiOutlined />,
      label: <StyledMenuLink to="/kivu/mpc-service" onClick={(e) => e.stopPropagation()}>MPC Service</StyledMenuLink>,
    },
    {
      key: 'notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
      children: [
        {
          key: 'critical-notifications',
          icon: <BellOutlined />,
          label: <StyledMenuLink to="/kivu/critical-notifications" onClick={(e) => e.stopPropagation()}>Critical Notifications</StyledMenuLink>,
        },
        {
          key: 'merchant-update-requests',
          icon: <BellOutlined />,
          label: <StyledMenuLink to="/kivu/merchant-update-requests" onClick={(e) => e.stopPropagation()}>Merchant Requests</StyledMenuLink>,
        },
        {
          key: 'webhooks',
          icon: <ApiOutlined />,
          label: <StyledMenuLink to="/kivu/webhooks" onClick={(e) => e.stopPropagation()}>Webhooks</StyledMenuLink>,
        },
      ],
    },
    {
      key: 'maintenance',
      icon: <SettingOutlined />,
      label: <StyledMenuLink to="/kivu/maintenance" onClick={(e) => e.stopPropagation()}>Maintenance</StyledMenuLink>,
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: <StyledMenuLink to="/kivu/settings" onClick={(e) => e.stopPropagation()}>Settings</StyledMenuLink>,
    },
    {
      key: 'launch-watcher',
      icon: <RocketOutlined />,
      label: (
        <LaunchWatcherButton
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            if (!WATCHER_URL) {
              message.error('Watcher URL is not configured. Please set REACT_APP_ONCHAIN_WATCHER_FRONTEND_URL in .env file');
              return;
            }
            const token = localStorage.getItem('accessToken') || '';
            const targetUrl = `${WATCHER_URL}?token=${encodeURIComponent(token)}`;
            window.open(targetUrl, '_blank', 'noopener,noreferrer');
          }}
        >
          <RocketOutlined className="icon" />
          <span>Launch Watcher</span>
        </LaunchWatcherButton>
      ),
      onClick: (e: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
        e.domEvent.stopPropagation();
        e.domEvent.preventDefault();
        // Click is handled by LaunchWatcherButton onClick
      },
    },
  ];

  // Add logout to mobile menu only
  const mobileMenuItems: MenuProps['items'] = [
    ...menuItems,
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  return (
    <DashboardProvider value={{ sessionExpiryTime, onSessionExpire: handleSessionExpire, userProfile }}>
      <StyledLayout>
        <StyledHeader>
          <LogoSection>
          <MobileMenuBtn 
            icon={<MenuOutlined />} 
            onClick={() => setMobileMenuVisible(true)} 
          />
          <Logo>
            <img 
              src="https://ekumahost.net/publicImages/switch/SWITCHICON.svg" 
              alt="Switch Wallet"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <span className="logo-text">Switch Wallet</span>
            <span className="badge">ADMIN</span>
          </Logo>
        </LogoSection>

        <RightSection>
          <NotificationBtn onClick={() => navigate('/kivu/critical-notifications')}>
            <BellOutlined style={{ fontSize: '20px' }} />
            {notificationCount > 0 && (
              <span className="count">{notificationCount > 99 ? '99+' : notificationCount}</span>
            )}
          </NotificationBtn>

          {userProfile && (
            <Tooltip 
              title={
                <div style={{ textAlign: 'right' }}>
                  <div>{userProfile.firstName} {userProfile.lastName}</div>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>{userProfile.email}</div>
                </div>
              } 
              placement="bottomRight"
            >
              <div
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
                onClick={() => navigate('/kivu/profile')}
              >
                {userProfile.countryCode && (
                  <ReactCountryFlag
                    countryCode={userProfile.countryCode}
                    svg
                    style={{
                      width: '1.5em',
                      height: '1.5em',
                      borderRadius: '2px'
                    }}
                  />
                )}
                <Avatar
                  src={userProfile.portraitUrl ? `data:image/jpeg;base64,${userProfile.portraitUrl}` : undefined}
                  icon={<UserOutlined />}
                  style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
                />
              </div>
            </Tooltip>
          )}
          
          <Tooltip title="Logout" placement="bottomRight">
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                color: COLORS.textSecondary,
                fontSize: '18px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
              className="logout-button"
            />
          </Tooltip>
        </RightSection>
      </StyledHeader>

      <Layout style={{ background: 'transparent', position: 'relative' }}>
        <StyledSider width={260}>
          <Menu
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            items={menuItems}
            onClick={handleMenuClick}
          />
        </StyledSider>
        
        <StyledContent style={{ position: 'relative', zIndex: 1 }}>
          <Outlet />
        </StyledContent>
      </Layout>

      {/* Mobile Drawer Menu */}
      <Drawer
        placement="left"
        onClose={() => setMobileMenuVisible(false)}
        open={mobileMenuVisible}
        width="85%"
        mask={true}
        maskClosable={true}
        closable={true}
        zIndex={1000}
        bodyStyle={{ 
          padding: 0, 
          background: 'transparent',
        }}
        headerStyle={{
          background: 'transparent',
          borderBottom: `1px solid ${COLORS.border}`,
          color: COLORS.textPrimary
        }}
        drawerStyle={{
          background: 'transparent',
          zIndex: 1001,
          maxWidth: '320px'
        }}
        maskStyle={{
          zIndex: 999
        }}
        title={
          <Logo>
            <img 
              src="https://ekumahost.net/publicImages/switch/SWITCHICON.svg" 
              alt="Switch Wallet"
              style={{ width: 24, height: 24 }}
            />
            <span className="logo-text" style={{ fontSize: 16 }}>Switch Wallet</span>
          </Logo>
        }
      >
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          items={mobileMenuItems}
          onClick={handleMenuClick}
          style={{ background: 'transparent', border: 'none' }}
        />
      </Drawer>
    </StyledLayout>
    </DashboardProvider>
  );
};

export default AdminDashboard;
